/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  StyleSheet,
  Linking,
} from 'react-native';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { ParamListBase } from '@react-navigation/native';
import {
  fetchComplaintById,
  fetchComplaintHistory,
  isExpectedRespondError,
  respondComplaint,
} from '../infra/complaintsRepo';
import {
  ALLEGED_PARTY_LABELS,
  ACTOR_KIND_LABELS,
  complaintReasonLabel,
  COMPLAINT_STATUS_LABELS,
  COMPLAINT_STATUS_COLORS,
  HISTORY_EVENT_LABELS,
  formatComplaintItemOptionsLabel,
  formatDeadlineLabel,
  hoursUntilDeadline,
  type ComplaintDetail,
  type ComplaintHistoryEntry,
} from '../domain/complaintTypes';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { usePendingOrdersCount } from '../../shell/providers/PendingOrdersCountProvider';
import { ZoomableImage } from '../../consultations/public-api';
import { formatArPhoneDisplay, telHref } from '@shared/phone';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { formatMoney as formatComplaintAmount } from '../../shared/format/public-api';
import { formatLocalDateTime } from '@shared/vueltoDateTime';

function PropertyRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={modalExperience.detailKvRow} testID={`complaint-detail-prop-${label}`}>
      <Text style={modalExperience.detailMetaLabel}>{label}</Text>
      <Text style={modalExperience.detailKvValueStrong} numberOfLines={3}>
        {value}
      </Text>
    </View>
  );
}

export function ComplaintDetailScreen() {
  const route = useRoute<RouteProp<ParamListBase, 'ComplaintDetail'>>();
  const complaintId = (route.params as { complaintId: string }).complaintId;
  const { refresh: refreshInboxCounts } = usePendingOrdersCount();
  const canReply = useModulePermission('BranchSettings', 'reply');
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id;
  const [complaint, setComplaint] = useState<ComplaintDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState<ComplaintHistoryEntry[]>([]);
  /** Track whether the user has manually edited the response field. */
  const responseDirty = useRef(false);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(false);
    try {
      const data = await fetchComplaintById(complaintId);
      setComplaint(data);
      // Only populate the response field on initial load, not when refreshing
      // (the user may be mid-edit and we don't want to clobber their work).
      if (!isRefresh && data?.kiosco_response) {
        setResponse(data.kiosco_response);
        responseDirty.current = false;
      }
      // Fetch history timeline (non-blocking — don't fail the screen if history fails)
      // #798: pasar orgId para filtrar por organización
      fetchComplaintHistory(complaintId, orgId)
        .then(setHistory)
        .catch((e) => console.warn('ComplaintDetailScreen history', e));
    } catch (e) {
      console.error('ComplaintDetailScreen load', e);
      setError(true);
    } finally {
      if (isRefresh) setRefreshing(false); else setLoading(false);
    }
  }, [complaintId, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  // B1: separate loading (initial) from error — show error state with retry
  if (loading) {
    return (
      <ShellContentFrame style={styles.center}>
        <Text style={styles.loadingText} testID="complaints.detail.loading">Cargando reclamo…</Text>
      </ShellContentFrame>
    );
  }

  if (error || !complaint) {
    return (
      <ShellContentFrame style={styles.center}>
        <Text style={styles.loadingText}>No se pudo cargar el reclamo.</Text>
        <TouchableOpacity
          style={styles.retryBtn}
          onPress={() => void load()}
          accessibilityRole="button"
          accessibilityLabel="Reintentar carga del reclamo"
          testID="complaint-detail-retry"
        >
          <Text style={styles.retryText}>Reintentar</Text>
        </TouchableOpacity>
      </ShellContentFrame>
    );
  }

  const hoursLeft = hoursUntilDeadline(complaint.deadline_at);
  const isUrgent = complaint.status === 'open' && hoursLeft < 24;
  const subjectRef =
    complaint.subject_kind === 'appointment'
      ? `Turno #${(complaint.appointment_id ?? complaint.id).slice(0, 8)}`
      : `Pedido #${complaint.display_code ?? (complaint.order_id ?? complaint.id).slice(0, 8)}`;
  const fulfillmentLabel =
    complaint.pickup_type === 'pickup'
      ? 'Retiro'
      : complaint.pickup_type === 'delivery'
        ? 'Envío'
        : null;
  const customerName = complaint.customer_name?.trim() || 'Cliente sin nombre';
  const phone = complaint.customer_phone?.trim() || null;
  // Si el nombre cayó al fallback del teléfono, no mostrarlo dos veces:
  // "Cliente" aclara que falta el nombre y "Teléfono" queda con el número.
  const nameIsPhone = phone !== null && customerName === formatArPhoneDisplay(phone);
  // B4: compute real countdown for pending_customer using customer_decision_deadline_at
  const pendingHoursLeft =
    complaint.status === 'pending_customer' && complaint.customer_decision_deadline_at
      ? hoursUntilDeadline(complaint.customer_decision_deadline_at)
      : null;

  const handleRespond = async () => {
    if (!canReply) {
      showError('Sin permiso', 'No podés responder reclamos.');
      return;
    }
    if (response.trim().length < 5) {
      showError('Muy corta', 'Escribí al menos 5 caracteres.');
      return;
    }
    setSubmitting(true);
    try {
      await respondComplaint(complaintId, response.trim());
      responseDirty.current = false;
      await load(true);
      void refreshInboxCounts();
      showSuccess(
        'Respuesta enviada',
        'El cliente tiene 48h para confirmar. Si no contesta, se cierra.',
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      if (msg && isExpectedRespondError(msg)) {
        showError('No se pudo enviar', msg);
        return;
      }
      presentUnexpectedFailure({
        module: 'complaints',
        op: 'respond',
        error: e,
        title: 'Error',
        userMessage: 'No se pudo responder.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ShellContentFrame style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => void load(true)}
            colors={[ticketColors.gold]}
            tintColor={ticketColors.gold}
          />
        }
      >
        <View style={styles.card}>
          <View style={styles.headerRow}>
            <Text style={styles.title} testID="complaints.detail.title">Reclamo #{complaint.id.slice(0, 8)}</Text>
            <View
              style={[styles.statusBadge, { backgroundColor: COMPLAINT_STATUS_COLORS[complaint.status] }]}
            >
              <Text style={styles.statusText}>{COMPLAINT_STATUS_LABELS[complaint.status]}</Text>
            </View>
          </View>
          <Text style={styles.date}>{formatLocalDateTime(complaint.created_at)}</Text>
          {complaint.status === 'open' ? (
            <Text style={[styles.deadline, isUrgent && styles.deadlineUrgent]}>
              {formatDeadlineLabel(complaint.deadline_at)}
            </Text>
          ) : null}
          {complaint.status === 'pending_customer' ? (
            <Text style={styles.deadline}>
              {pendingHoursLeft !== null
                ? `Esperando al cliente (${formatDeadlineLabel(complaint.customer_decision_deadline_at!)}). Si no contesta, se cierra.`
                : 'Esperando al cliente (48h). Si no contesta, se cierra.'}
            </Text>
          ) : null}
          {complaint.status === 'escalated' ? (
            <View style={styles.escalatedBanner}>
              <Text style={styles.escalatedText}>
                Pasó a Vuelto. Ya no lo cerrás vos. Vuelto decide. Te avisamos cuando quede.
              </Text>
            </View>
          ) : null}
        </View>

        {/* Stripe/Linear-style properties: label left, value right */}
        <View style={styles.card}>
          <PropertyRow label="Motivo" value={complaintReasonLabel(complaint.reason)} />
          {/* B12: hide "De quién" when merchant (consistent with list view) */}
          {complaint.alleged_party !== 'merchant' ? (
            <PropertyRow
              label="De quién"
              value={ALLEGED_PARTY_LABELS[complaint.alleged_party] ?? complaint.alleged_party}
            />
          ) : null}
          <PropertyRow label="Cliente" value={nameIsPhone ? 'Cliente sin nombre' : customerName} />
          {phone ? (
            <TouchableOpacity
              onPress={() => {
                const href = telHref(phone);
                if (href) {
                  Linking.openURL(href).catch(() =>
                    showError('No se pudo abrir', 'No hay app de teléfono disponible.'),
                  );
                }
              }}
              accessibilityRole="link"
              accessibilityLabel={`Llamar a ${formatArPhoneDisplay(phone)}`}
              testID="complaint-detail-call"
            >
              <PropertyRow label="Teléfono" value={formatArPhoneDisplay(phone)} />
            </TouchableOpacity>
          ) : null}
          <PropertyRow label={complaint.subject_kind === 'appointment' ? 'Turno' : 'Pedido'} value={subjectRef} />
          {fulfillmentLabel ? <PropertyRow label="Entrega" value={fulfillmentLabel} /> : null}
          {complaint.subject_kind !== 'appointment' ? (
            <PropertyRow label="Total" value={formatComplaintAmount(complaint.order_total)} />
          ) : complaint.appointment_label ? (
            <PropertyRow label="Detalle" value={complaint.appointment_label} />
          ) : null}

          {complaint.subject_kind !== 'appointment' && complaint.items.length > 0 ? (
            <View style={styles.itemsBlock}>
              {complaint.items.map((item, idx) => {
                const opts = formatComplaintItemOptionsLabel(item.options);
                const note = item.line_note?.trim() || '';
                return (
                  <View key={`${item.product_name}-${idx}`} style={styles.itemBlock}>
                    <Text style={styles.itemLine}>
                      {item.quantity}× {item.product_name} — {formatComplaintAmount(Number(item.unit_price))}
                    </Text>
                    {opts ? <Text style={styles.itemMeta}>{opts}</Text> : null}
                    {note ? <Text style={styles.itemMeta}>Nota: {note}</Text> : null}
                  </View>
                );
              })}
            </View>
          ) : null}
        </View>

        <View style={styles.card}>
          <Text style={styles.blockLabel}>Qué pasó</Text>
          <Text style={styles.descText}>{complaint.description}</Text>
        </View>

        {complaint.photo_urls.length > 0 ? (
          <View style={styles.card}>
            <Text style={styles.blockLabel}>Fotos</Text>
            <View style={styles.photoGrid}>
              {complaint.photo_urls.map((url, idx) => (
                <ZoomableImage
                  key={`${url}-${idx}`}
                  uri={url}
                  title={`Foto ${idx + 1}`}
                  style={styles.photoThumb}
                />
              ))}
            </View>
          </View>
        ) : null}

        {history.length > 0 ? (
          <View style={styles.card}>
            <Text style={styles.blockLabel}>Historial</Text>
            {history.map((entry, idx) => {
              const label = HISTORY_EVENT_LABELS[entry.event_type] ?? entry.event_type;
              const actor = ACTOR_KIND_LABELS[entry.actor_kind] ?? entry.actor_kind;
              const fromLabel = entry.from_status
                ? COMPLAINT_STATUS_LABELS[entry.from_status as keyof typeof COMPLAINT_STATUS_LABELS] ?? entry.from_status
                : null;
              const toLabel = entry.to_status
                ? COMPLAINT_STATUS_LABELS[entry.to_status as keyof typeof COMPLAINT_STATUS_LABELS] ?? entry.to_status
                : null;
              return (
                <View key={entry.id} style={styles.historyItem}>
                  <View style={styles.historyDot} />
                  <View style={styles.historyContent}>
                    <Text style={styles.historyEvent}>{label}</Text>
                    {fromLabel && toLabel ? (
                      <Text style={styles.historyMeta}>{fromLabel} → {toLabel}</Text>
                    ) : toLabel ? (
                      <Text style={styles.historyMeta}>Estado: {toLabel}</Text>
                    ) : null}
                    <Text style={styles.historyDate}>
                      {formatLocalDateTime(entry.created_at)} · {actor}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        ) : null}

        {complaint.status === 'open' ||
        (complaint.status === 'pending_customer' &&
          (complaint.response_revision_count ?? 0) < 1) ? (
          <View style={styles.card} testID="complaints.detail.response-section">
            <Text style={styles.blockLabel}>
              {complaint.status === 'open' ? 'Responder' : 'Corregir respuesta'}
            </Text>
            {canReply ? (
              <>
                <Text style={styles.descText}>
                  {complaint.status === 'open'
                    ? 'El cliente tiene 48h para confirmar. Si no contesta, se cierra.'
                    : 'Podés corregir una sola vez. Se reinician las 48h.'}
                </Text>
                <TextInput
                  style={styles.textArea}
                  multiline
                  numberOfLines={4}
                  placeholder="Escribí tu respuesta…"
                  placeholderTextColor={ticketColors.ink3}
                  value={response}
                  onChangeText={(text) => {
                    responseDirty.current = true;
                    setResponse(text);
                  }}
                  testID="complaint-response-input"
                />
                <TouchableOpacity
                  style={[styles.actionBtn, styles.primaryBtn]}
                  onPress={() => void handleRespond()}
                  disabled={submitting}
                  accessibilityRole="button"
                  accessibilityLabel={
                    complaint.status === 'open' ? 'Enviar respuesta' : 'Reenviar respuesta'
                  }
                  accessibilityState={{ disabled: submitting }}
                  testID="complaint-send-response"
                >
                  <Text style={styles.primaryBtnText}>
                    {submitting
                      ? 'Enviando…'
                      : complaint.status === 'open'
                        ? 'Enviar respuesta'
                        : 'Reenviar respuesta'}
                  </Text>
                </TouchableOpacity>
              </>
            ) : (
              <Text style={styles.descText}>No podés responder reclamos.</Text>
            )}
          </View>
        ) : complaint.kiosco_response ? (
          <View style={styles.card} testID="complaints.detail.response-sent">
            <Text style={styles.blockLabel}>Respuesta del local</Text>
            <Text style={styles.descText}>{complaint.kiosco_response}</Text>
          </View>
        ) : null}
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: ticketColors.paper },
  scroll: { padding: 12, paddingBottom: 32, gap: 8 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { fontSize: 16, color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  card: {
    backgroundColor: ticketColors.card,
    borderRadius: 10,
    padding: 16,
    borderWidth: 1,
    borderColor: ticketColors.border,
    gap: 10,
  },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  title: { flex: 1, fontSize: 18, fontWeight: '700', color: ticketColors.ink, fontFamily: ticketFonts.sans },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  statusText: { fontSize: 11, fontWeight: '700', color: '#fff' },
  date: { fontSize: 12, color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  deadline: { fontSize: 13, fontWeight: '600', color: ticketColors.gold, fontFamily: ticketFonts.sans },
  deadlineUrgent: { color: ticketColors.bad },
  escalatedBanner: { backgroundColor: '#FF6B6B20', borderRadius: 8, padding: 8 },
  escalatedText: {
    color: ticketColors.bad,
    fontWeight: '600',
    fontSize: 13,
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  blockLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontFamily: ticketFonts.sans,
  },
  itemsBlock: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.border,
    paddingTop: 10,
    gap: 6,
  },
  itemBlock: { gap: 1 },
  itemLine: { fontSize: 13, color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  itemMeta: { fontSize: 12, color: ticketColors.ink3, fontFamily: ticketFonts.sans },
  descText: { fontSize: 14, color: ticketColors.ink, lineHeight: 20, fontFamily: ticketFonts.sans },
  photoGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  photoThumb: {
    width: 104,
    height: 104,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
  },
  historyItem: {
    flexDirection: 'row',
    gap: 10,
    paddingVertical: 6,
  },
  historyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ticketColors.gold,
    marginTop: 6,
  },
  historyContent: { flex: 1, gap: 2 },
  historyEvent: { fontSize: 14, fontWeight: '600', color: ticketColors.ink, fontFamily: ticketFonts.sans },
  historyMeta: { fontSize: 13, color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  historyDate: { fontSize: 11, color: ticketColors.ink3, fontFamily: ticketFonts.sans },
  textArea: {
    borderWidth: 1,
    borderColor: ticketColors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  actionBtn: { borderRadius: 10, paddingVertical: 14, alignItems: 'center' },
  primaryBtn: { backgroundColor: ticketColors.gold },
  primaryBtnText: { color: ticketColors.goldInk, fontSize: 16, fontWeight: '700', fontFamily: ticketFonts.sans },
  retryBtn: {
    marginTop: 12,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
  },
  retryText: { fontSize: 14, fontWeight: '700', color: ticketColors.goldInk, fontFamily: ticketFonts.sans },
});
